import { ChevronDownIcon, SearchIcon } from "@heroicons/react/outline";
import { differenceInDays } from "date-fns";
import React, { useRef } from "react";
import { UserFilled } from "ui/components/icons";
import { getValidDate } from "ui/lib/helpers";
import { cn } from "ui/lib/utils";
import { ActionMenu } from "apollo/components/mobile/action-menu";
import { BottomSheet } from "apollo/components/mobile/bottom-sheet";
import { ListRow } from "apollo/components/mobile/list-row";
import { LoadState } from "apollo/components/mobile/load-state";
import { usePushedView } from "apollo/components/mobile/pushed-view";
import {
  CommandCenterMembersSearchProvider,
  SEARCH_TIMEOUT_MS,
  useCommandCenterMembersSearch,
} from "./containers/members/search";
import { SearchCoverage } from "./search-coverage";

/** `?searchSheet=open`, so Back closes the phone search. */
export const useSearchSheet = () => usePushedView("searchSheet");

const lastActive = (value?: string) => {
  if (!value) return "";
  const days = differenceInDays(new Date(), getValidDate(value));
  return days ? `active ${days}d ago` : "active today";
};

const Avatar = ({ url }: { url?: string }) =>
  url ? (
    <span
      aria-hidden="true"
      style={{ backgroundImage: `url(${url}?w=80)` }}
      className="block size-10 rounded-full bg-white/[0.06] bg-cover bg-center"
    />
  ) : (
    <span
      aria-hidden="true"
      className="flex size-10 items-center justify-center rounded-full bg-white/[0.06]"
    >
      <UserFilled className="w-4 text-white/60" />
    </span>
  );

const Results = ({
  inputRef,
}: {
  inputRef: React.RefObject<HTMLInputElement>;
}) => {
  const {
    inputValue,
    setInputValue,
    filteredData,
    query,
    searching,
    searchingAll,
    partial,
    hasNextPage,
    fetchNextPage,
    isFetching,
    error,
    retry,
    teamID,
    setTeamID,
    bootcamps,
  } = useCommandCenterMembersSearch();
  const team = bootcamps?.find(({ id }) => id === teamID);

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-2 px-2 pb-3">
        <label className="relative flex min-w-0 flex-1 items-center">
          <span className="sr-only">Search members</span>
          <SearchIcon
            aria-hidden="true"
            className="pointer-events-none absolute left-3 size-5 text-white/50"
          />
          <input
            ref={inputRef}
            type="search"
            enterKeyHint="search"
            autoComplete="off"
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            placeholder="Name, email or member ID"
            className="h-11 w-full rounded-xl bg-white/[0.06] pl-10 pr-3 text-[16px] text-white/90 placeholder:text-white/45 focus:outline-none focus:ring-2 focus:ring-primary/60"
          />
        </label>
        <ActionMenu
          title="Filter by team"
          options={[
            { value: "", label: "All teams", selected: !teamID },
            ...(bootcamps || []).map(({ id, name }) => ({
              value: id,
              label: name,
              selected: id === teamID,
            })),
          ]}
          onSelect={(value) => setTeamID(value)}
          trigger={
            <button
              type="button"
              aria-label={`Team: ${team?.name || "All teams"}. Change team`}
              className={cn(
                "flex h-11 max-w-[40%] shrink-0 items-center gap-1 rounded-full px-3.5 text-[14px] font-medium",
                team
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground"
              )}
            >
              <span className="truncate">{team?.name || "All teams"}</span>
              <ChevronDownIcon aria-hidden="true" className="size-4 shrink-0" />
            </button>
          }
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {!query ? (
          <p className="px-4 py-10 text-center text-[15px] text-white/60">
            Search members by name, email or member ID.
          </p>
        ) : (
          <LoadState
            loading={searching || (searchingAll && !filteredData.length)}
            error={error}
            empty={!filteredData.length}
            what="Search is unavailable."
            emptyMessage={`No matches for “${query}”`}
            onRetry={retry}
            timeoutMs={SEARCH_TIMEOUT_MS + 3000}
          >
            <ul aria-label="Members" className="space-y-0.5 pb-2">
              {filteredData.map((member) => (
                <li key={member.id}>
                  <ListRow
                    href={`/members/${member.id}`}
                    leading={<Avatar url={member.profileImg} />}
                    title={member.label}
                    subtitle={[
                      member.bootcampName || "Onboarding",
                      lastActive(member.lastActivityTS),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  />
                </li>
              ))}
            </ul>
            {hasNextPage ? (
              <div className="flex justify-center pb-3">
                <button
                  type="button"
                  disabled={isFetching}
                  onClick={() => fetchNextPage()}
                  className="min-h-11 rounded-lg bg-white/[0.08] px-4 text-[15px] text-white/90 active:bg-white/[0.12] disabled:opacity-50"
                >
                  {isFetching ? "Loading…" : "Show more"}
                </button>
              </div>
            ) : null}
            <SearchCoverage searchingAll={searchingAll} partial={partial} />
          </LoadState>
        )}
      </div>
    </div>
  );
};

/**
 * Member search on a phone: a full-height sheet with one column of members,
 * each opening their page. Tablets and desktop keep the palette.
 */
export const MemberSearchSheet = () => {
  const sheet = useSearchSheet();
  const open = sheet.value === "open";
  const input = useRef<HTMLInputElement>(null);

  return (
    <BottomSheet
      full
      urlView="searchSheet"
      open={open}
      onOpenChange={(next) => !next && sheet.close()}
      title="Search"
      initialFocus={input}
    >
      {open ? (
        <CommandCenterMembersSearchProvider>
          <Results inputRef={input} />
        </CommandCenterMembersSearchProvider>
      ) : null}
    </BottomSheet>
  );
};
